import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Trash2, Plus, Minus, ArrowRight, Tag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";
import { CART } from "@/constants/testIds";
import { toast } from "sonner";
import ProductImage from "@/components/ProductImage";

export default function Cart() {
  const { items, remove, setQty, subtotal } = useCart();
  const [aff, setAff] = useState("");
  const [discount, setDiscount] = useState(0);
  const nav = useNavigate();

  const tax = Math.round(subtotal * 0.18);
  const discountAmt = Math.round(subtotal * (discount / 100));
  const total = subtotal + tax - discountAmt;

  const applyAffiliate = async () => {
    try {
      const { data } = await api.post(`/affiliates/validate/${aff.toUpperCase()}`);
      if (data.valid) {
        setDiscount(data.discount_pct || 0);
        toast.success(`Code applied — ${data.discount_pct || 0}% off`);
      } else {
        setDiscount(0);
        toast.error("Invalid code");
      }
    } catch { toast.error("Could not validate code"); }
  };

  if (items.length === 0) {
    return (
      <div className="py-32 text-center">
        <div className="text-white/60">Your cart is empty.</div>
        <Link to="/marketplace" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full gradient-bg">
          Browse marketplace <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter">Your cart</h1>
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {items.map((i) => (
              <div key={i.product_id} data-testid={CART.itemRow(i.product_id)}
                   className="glass rounded-2xl p-4 flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/5 shrink-0">
                  <ProductImage src={i.image} alt={i.name} iconSize="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{i.sku}</div>
                  <div className="text-sm font-medium truncate">{i.name}</div>
                  <div className="text-xs text-white/50 mt-1">₹{i.price.toLocaleString("en-IN")} each</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setQty(i.product_id, i.qty - 1)} className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                  <input data-testid={CART.qtyInput(i.product_id)} value={i.qty}
                         onChange={(e) => setQty(i.product_id, parseInt(e.target.value || "1"))}
                         className="w-10 bg-transparent text-center" />
                  <button onClick={() => setQty(i.product_id, i.qty + 1)} className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                </div>
                <div className="font-display text-accent w-24 text-right">₹{(i.price * i.qty).toLocaleString("en-IN")}</div>
                <button data-testid={CART.removeBtn(i.product_id)} onClick={() => remove(i.product_id)}
                        className="text-white/40 hover:text-white">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="glass rounded-2xl p-6 h-fit sticky top-24">
            <h3 className="font-display text-xl">Summary</h3>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span data-testid={CART.subtotal}>₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-white/60">GST 18%</span><span data-testid={CART.tax}>₹{tax.toLocaleString("en-IN")}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-300"><span>Affiliate discount</span><span>−₹{discountAmt.toLocaleString("en-IN")}</span></div>
              )}
              <div className="h-px bg-white/10 my-3" />
              <div className="flex justify-between font-display text-lg"><span>Total</span><span data-testid={CART.total} className="text-accent">₹{total.toLocaleString("en-IN")}</span></div>
            </div>

            <div className="mt-6">
              <label className="text-xs uppercase tracking-widest text-white/40 font-mono flex items-center gap-1"><Tag className="w-3 h-3" /> Affiliate / discount code</label>
              <div className="mt-2 flex gap-2">
                <input data-testid={CART.affiliateCode} value={aff} onChange={(e) => setAff(e.target.value)}
                       placeholder="AFF-XXXXXX"
                       className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm uppercase outline-none focus:border-[#9D4CDD]" />
                <button data-testid={CART.applyAffiliate} onClick={applyAffiliate}
                        className="text-sm px-4 py-2 rounded-full border border-white/15 hover:bg-white/5">Apply</button>
              </div>
            </div>

            <button data-testid={CART.checkoutBtn} onClick={() => nav("/checkout", { state: { affiliate: aff, discount } })}
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium">
              Proceed to checkout <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
